import { numberSeparator, toPersianNumber } from "@/hooks/useThousandsSeparator";
import CustomTable from "@/shared/components/common/table";
import BoxKit from "@/shared/components/kit/box";
import CustomButton from "@/shared/components/kit/button";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { Grid, TextField } from "@mui/material";
import { useState } from "react";
import AdapterMomentJalali from "@/shared/components/common/datePeacker";
import CustomSelect from "@/shared/components/common/select";
import { useQuery } from "@tanstack/react-query";
import { fetchListAllTransactionList } from "@/api/merchant/request.get";
import moment from "jalali-moment";
import { MerchantTransaction, MerchantTransactionResponse } from "@/types/api/merchant/merchant.type";
import ContentSkeleton from "@/modules/user/components/walletReports/content.skeleton";

export const Content = () => {
  // Change default value to 10 for consistency
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchText, setSearchText] = useState<string>("");

  const { data: dataListChart, isLoading, isFetching } = useQuery<MerchantTransactionResponse, Error>({
    queryKey: ["fetchListAll-dataTransAction", currentPage, searchText, itemsPerPage],
    queryFn: async (): Promise<MerchantTransactionResponse> => {
      const res = await fetchListAllTransactionList(currentPage, searchText, itemsPerPage);
      return res;
    },
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });

  const handleChangeDate = () => { };

  const handleItemsPerPageChange = (newValue: number) => {
    // Reset to page 1 when items per page changes
    setCurrentPage(1);
    setItemsPerPage(newValue);
  };

  const dataHeader = [
    { id: "rowId", title: "ردیف" },
    { id: "amount", title: "مبلغ(ریال)" },
    { id: "ts", title: "ساعت و تاریخ " },
    { id: "ref_type", title: "نوع تراکنش" },
    { id: "source_card_number", title: "مبدا تراکنش" },
    { id: "destination_card_number", title: "مقصد تراکنش" },
    { id: "ref_id", title: "کد رهگیری تراکنش" },
    { id: "test3", title: "توضیحات" },
  ];

  const dataNew = dataListChart?.all_merchant_transactions?.map(
    (item: MerchantTransaction, index: number) => {
      // Calculate correct index based on current page and items per page
      const rowIndex = (currentPage - 1) * itemsPerPage + index + 1;
      
      const refTypeMap: Record<string, string> = {
        transfer: "انتقال",
        order: "سفارش",
        "pro-ref-earn": "کارمزد چند سطحی حرفه‌ای",
        "ref-earn": "کارمزد تک سطحی",
        withdraw: "برداشت",
        installments: "اقساط",
        deposite: "افزایش موجودی",
        creditScoreFee: "کارمزد اعتبارسنجی",
        resellerOrder: "-",
        bistCard: "پولی پی",
        edit: "ویرایش",
        "pos-pazirande": "دستگاه پوز",
        "pos-ghishe": "-",
        wallet: "کیف پول",
        ipg: "Ipg",
        "edit-pazirande": "اصلاح توسط پذیرنده",
        gift_fee: "-",
        emv: "خرید emv",
        qr_code: "QR Code",
        loam: "وام",
      };

      return {
        ...item,
        rowId: toPersianNumber(rowIndex),
        amount: numberSeparator(item.amount),
        ts: item.ts ? toPersianNumber(`${moment.unix(item.ts).locale("fa").format("HH:mm")} ${moment.unix(item.ts).locale("fa").format("jYYYY/jMM/jDD")}`) : '-',
        source_card_number: item.source_card_number ? (<TypoGraphyKit variant="body1" sx={{ direction: "ltr" }}>{toPersianNumber(item.source_card_number)}</TypoGraphyKit>) : ("-"),
        destination_card_number: item.destination_card_number ? (<TypoGraphyKit variant="body1" sx={{ direction: "ltr" }}>{toPersianNumber(item.destination_card_number)}</TypoGraphyKit>) : ("-"),
        ref_type: refTypeMap[item.ref_type] || item.ref_type,
        ref_id: item.ref_id ? toPersianNumber(item.ref_id) : '-',
      };
    }
  ) ?? [];

  const handleDownloadCSV = () => {
    if (!dataListChart?.all_merchant_transactions || dataListChart?.all_merchant_transactions.length === 0) return;

    const headers = [
      "ردیف",
      "مبلغ برداشت (ریال)",
      "ساعت و تاریخ درخواست",
      "مبدا تراکنش",
      "مقصد تراکنش",
      "وضعیت درخواست",
      "توضیحات",
    ];

    const rows = dataListChart?.all_merchant_transactions.map((item: any, index: number) => {
      const rowIndex = (currentPage - 1) * itemsPerPage + index + 1;
      return [
        toPersianNumber(rowIndex),
        item.amount ? toPersianNumber(numberSeparator(item.amount)) : '',
        item.ts ? moment.unix(item.ts).locale("fa").format("jYYYY/jMM/jDD HH:mm") : '',
        item.ref_type ? toPersianNumber(item.ref_type) : '',
        item.source_card_number ? toPersianNumber(item.source_card_number) : '',
        item.destination_card_number ? toPersianNumber(item.destination_card_number) : '',
        item.ref_id ? toPersianNumber(item.ref_id) : '',
      ];
    });

    const csvContent = [headers, ...rows].map(e => e.map((v: any) => `"${v}"`).join(",")).join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });

    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "transactions.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <StackKit sx={{ width: "100%", px: { xs: 2, sm: 4, md: 5 } }} gap={3}>
      <Grid container spacing={2}>
        {/* Header Row: Total Sale + Refresh */}
        <Grid size={{ xs: 12 }}>
          <StackKit direction={{ xs: "column", sm: "row" }} justifyContent={{ sm: 'start', xs: 'center' }} width={'100%'} alignItems={{ xs: "flex-start", sm: "center" }} gap={2}>
            <TypoGraphyKit textAlign={{ xs: 'center', sm: 'right' }} width={'100%'} variant="body1">مجموع فروش پذیرنده</TypoGraphyKit>
            <StackKit direction="row" justifyContent={{ sm: 'start', xs: 'center' }} gap={2} flexWrap="wrap">
              <TypoGraphyKit variant="subtitle1">
                {numberSeparator(dataListChart?.meta?.total_sale)}ریال
              </TypoGraphyKit>
            </StackKit>
          </StackKit>
        </Grid>

        {/* Filters Row: Select + Date + Search */}
        <Grid size={{ xs: 12 }}>
          <StackKit direction={{ xs: "column", sm: 'column', md: "row" }} justifyContent="space-between" alignItems="center">
            <CustomSelect
              label={
                <TypoGraphyKit variant="body1" sx={{ color: "#A4A4A4" }}>
                  تعداد در هر صفحه
                </TypoGraphyKit>
              }
              options={[5, 10, 15, 20]}
              onChange={handleItemsPerPageChange}
              value={itemsPerPage}
            />

            <StackKit direction={{ xs: "column", sm: "row" }} justifyContent="flex-end" alignItems="center" gap={2} sx={{ width: "100%" }}>
              <BoxKit>
                <AdapterMomentJalali handleChange={handleChangeDate} heightProps="48px" />
              </BoxKit>
              <TextField 
                placeholder="وارد کردن جزئیات جستجو" 
                onChange={(e) => {
                  // Reset to page 1 when search changes
                  setCurrentPage(1);
                  setSearchText(e.target.value);
                }} 
              />
              <CustomButton sx={{ px: 3, whiteSpace: "nowrap", height: "38px" }}>
                <TypoGraphyKit variant="body1">جستجو</TypoGraphyKit>
              </CustomButton>
            </StackKit>
          </StackKit>
        </Grid>

        {/* Table */}
        <Grid size={{ xs: 12 }} sx={{ position: "relative" }}>
          <BoxKit sx={{ overflowX: "auto" }}>
            {isLoading || isFetching ?
              <ContentSkeleton />
              :
              <CustomTable 
                dataHeader={dataHeader} 
                dataTbody={dataNew} 
                total={dataListChart?.meta?.total || 0} 
                setPages={setCurrentPage} 
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
              />
            }
          </BoxKit>
        </Grid>

        {/* Download CSV */}
        <Grid size={{ xs: 12 }}>
          <StackKit direction="row" justifyContent={{ xs: "center", sm: "flex-end" }} alignItems="center">
            <CustomButton sx={{ width: "fit-content", bgcolor: "#5A5A5A", whiteSpace: "nowrap" }} onClick={handleDownloadCSV}>
              <TypoGraphyKit variant="body1">دریافت صورتحساب به صورت فایل</TypoGraphyKit>
            </CustomButton>
          </StackKit>
        </Grid>
      </Grid>
    </StackKit>
  );
};